import React from 'react'
import styled from 'styled-components'

import Card from 'components/Card'
import KeyboardAccessible from 'components/KeyboardAccessible'
import Tooltip from 'components/Tooltip'

import determineCardLayout from 'pages/Device/SystemDashboard/determineCardLayout'

import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'
import useScreenSize from 'hooks/useScreenSize'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

type CardProps = {
  link: string
  label: string
  icon?: string
  toolTipText?: string
  enabled: boolean
  details?: React.ReactNode | null,
  premiumTabName?: string,
  premiumDefaultHover?: string
}

type navigationCallbackProps = {
  selectedTab: string
  ga4Category: string
  ga4Action: string
  ga4Label: string
}

type Props = {
  tabs: CardProps[]
  navigationCallback: (value: navigationCallbackProps) => void
  isZoned: boolean
}

type ICard = {
  tab: string
  order: number
  pingdomId: string
}

const DETAIL_TABS = new Set(['status', 'notes', 'config-components', 'alerts', 'reports'])

const SystemDashboard = ({ tabs, navigationCallback, isZoned }: Props) => {
  const screenSize = useScreenSize()

  const cardLayout = determineCardLayout({ isZoned })

  const layout = screenSize === 'small' ? cardLayout.flat() : cardLayout
  const { LockIcon } = usePremiumFeaturesContext()

  const renderCardContent = (tab: CardProps) => (
    <Tooltip title={tab.toolTipText || ''} placement="bottom">
      <S.CardContent
        onClick={() => {
          if (tab.enabled) {
            navigationCallback({
              selectedTab: tab.link,
              ga4Category: 'System Dashboard',
              ga4Action: 'User Clicks a card on the System Dashboard page',
              ga4Label: tab.label
            })
          }
        }}
      >
        {DETAIL_TABS.has(tab.link) ? (
          tab.details
        ) : (
          <>
            {tab.premiumTabName && (
              <LockIcon
                featureCode={tab.premiumTabName}
                defaultHover={tab.premiumDefaultHover}
                canShowTooltip={false}
              />
            )}
            <S.Title enabled={tab.enabled}>{tab.label}</S.Title>
            {tab.icon && (
              <S.Img
                enabled={tab.enabled}
                alt={tab.label}
                src={`/img/${tab.icon}`}
              />
            )}
          </>
        )}
      </S.CardContent>
    </Tooltip>
  )

  const layoutWithOrder = (card: ICard) => {
    const tabData = tabs.find(t => t.link === card.tab)
    return tabData ? (
      <S.Card key={tabData.link} order={card.order} enabled={tabData.enabled} pingdomId={card.pingdomId}>
        {renderCardContent(tabData)}
      </S.Card>
    ) : null
  }

  return (
    <S.DeviceWrapper>
      {layout.map((row, rowIndex) => (
        screenSize === 'small'
          ? layoutWithOrder(row as ICard)
          : (
            <S.Container key={`row-${rowIndex + 1}`}>
              {(row as ICard[]).map((card) => {
                return layoutWithOrder(card)
              })}
            </S.Container>
          )
      ))}
    </S.DeviceWrapper>
  )
}

export default SystemDashboard

const S = {
  DeviceWrapper: styled.div`
    padding: 10px;
    display: flex;
    gap: 10px;

    @media ${screenSize.small} {
      display: grid;
      justify-content: center;
      align-items: center;
      gap: 10px;
    }
    @media ${screenSize.mediumOnly} {
      display: grid;
      grid-template-columns: 1fr 2fr;
    }
  `,
  Container: styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 375px;

    @media ${screenSize.mediumOnly} {
      width: 375px;
    }
    @media ${screenSize.small} {
      width: 330px;
    }
  `,
  Card: styled(Card).withConfig({
    shouldForwardProp: (prop) => !['enabled', 'order'].includes(prop)
  })<{enabled: boolean, order: number}>`
    order: ${({ order }) => order};
    display: flex;
    width: 100%;
    height: fit-content;
    align-items: center;
    justify-content: center;
    padding: 0px;
    box-shadow: 0 4px 8px ${color('shadowSubtle')};
    border: 1.4px solid transparent;
    border-radius: 10px;
    background-color: ${color('dashboardCardBackground')};
    position: relative;

    &:hover {
      border-color: ${({ enabled }) => enabled ? color('dashboardCardHoverBorder') : 'transparent'};
      cursor: pointer;
    }
  `,
  CardContent: styled(KeyboardAccessible)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: auto;
    padding: 15px;
  `,
  Title: styled.h1.withConfig({
    shouldForwardProp: (prop) => !['enabled'].includes(prop)
  })<{enabled: boolean}>`
    color: ${({ enabled }) => (enabled ? '' : color('disabledText'))};
    text-transform: capitalize;
    font-size: 20px;
    font-weight: 700;
    margin: 10px 0px;

    @media ${screenSize.small} {
      font-size: 18px;
      margin: 5px 0px;
    }
`,
  LastUpdatedAt: styled.h2`
    text-transform: capitalize;
    font-size: 14px;
    font-weight: 400;
    color: ${color('LastUpdatedText')};
    margin: 10px 0px;

    @media ${screenSize.small} {
      font-size: 11px;
    }
`,
  Img: styled.img.withConfig({
    shouldForwardProp: (prop) => !['enabled'].includes(prop)
  })<{enabled: boolean}>`
    filter: ${({ enabled }) => (enabled ? '' : 'grayscale(1)')};
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 58px;
    min-height: 58px;

    @media ${screenSize.small} {
      max-height: 48px;
      min-height: 48px;
      max-width: 125px;
    }
  `
}
